import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DateValueType } from "react-tailwindcss-datepicker";

import { ListSourceType } from "@/enum/list.enum";

import { useGetLeanListQuery } from "../../../redux/list/list.api";
import { CustomListsAutocompleteForSequencerItem } from "../../List/components/CustomListsAutocompleteForSequencer";
import { List } from "../../List/schema/list";
import { IFilterWithValues } from "../../List/components/FilterModal";

import {
	AnalyticsListContentFilterKey,
	AnalyticsListOptions,
	useAnalyticsListOptions,
} from "./useAnalyticsListOptions";
import { toSelectedList } from "../utils/selectedList";

import { CampaignChip } from "../schema/campaignChip";
import {
	ANALYTICS_FILTER_PARAM_KEY,
	addOrReplaceFilter,
	parseFilters,
	removeFilter,
	serializeFilters,
} from "../utils/activeFilters";
import {
	ANALYTICS_FROM_PARAM_KEY,
	ANALYTICS_LISTS_PARAM_KEY,
	ANALYTICS_TO_PARAM_KEY,
	parseDateRange,
	parseSelectedLists,
	ANALYTICS_SENDERS_PARAM_KEY,
	parseSenderUserIds,
	prefixedParamKey,
	serializeDateRange,
	serializeSenderUserIds,
	serializeSelectedLists,
	setOrDeleteParam,
} from "../utils/analyticsUrlState";
import { isLifetimeStart } from "../utils/lifetime";
import { AnalyticsRequest } from "../schema/analytics";

export interface AnalyticsSelection {
	dateFromToValue: DateValueType;
	onDateChange: (value: DateValueType) => void;

	/** The lists the filter can offer, plus its search / paging / quick-filter controls. */
	listOptions: AnalyticsListOptions;
	/** The loaded page of lists. Alias of `listOptions.lists`, for lookups by id. */
	listData: List[];
	selectedLists: CustomListsAutocompleteForSequencerItem[];
	onListSelect: (value: CustomListsAutocompleteForSequencerItem[]) => void;
	/**
	 * Applies a content quick filter: narrows the options *and* selects everything it
	 * matches, so "All lists with campaigns" scopes the report to exactly those lists.
	 */
	onListContentFilterToggle: (key: AnalyticsListContentFilterKey) => Promise<void>;

	campaigns: CampaignChip[];
	onCampaignToggle: (campaign: CampaignChip) => void;

	/** Sending users the report is narrowed to. Empty means every sender. */
	senderUserIds: number[];
	onSenderChange: (userIds: number[]) => void;

	/**
	 * Narrows the selection *and* sets extra query params in the same URL write.
	 *
	 * Needed because this hook mirrors its own state into the query string: a caller that
	 * writes params itself races that sync and one of the two writes is lost. Routing the
	 * whole change through here keeps a single writer.
	 */
	selectListsWithParams: (
		lists: CustomListsAutocompleteForSequencerItem[],
		params: Record<string, string>
	) => void;

	activeFilters: IFilterWithValues;
	addFilter: (filter: IFilterWithValues) => void;
	removeFilter: (filterKey: string) => void;
	clearFilters: () => void;

	/** The request body shared by the report fetch and the XLSX export. */
	payload: AnalyticsRequest;
}

export interface UseAnalyticsSelectionOptions {
	/**
	 * URL param prefix for a secondary column (see `COMPARISON_PARAM_PREFIX`). Omit for
	 * the primary selection, which keeps the un-prefixed keys so old links still work.
	 */
	paramPrefix?: string;
	/**
	 * Restricts the List filter's options to one source. The Signals tab passes `TRACKER`:
	 * signals only exist for tracked lists, so offering the rest invites a selection that
	 * can only ever report zeros.
	 */
	listSourceType?: ListSourceType;
}

/**
 * Owns everything the Analytics views select over — lists, per-campaign toggles, the
 * date range and the drill-down filters — and mirrors all of it into the URL so a
 * refresh or a shared link restores the same report.
 *
 * The primary instance lives above the tabs because the selection is shared: switching
 * between the overview and a deep dive must not reset which lists you were looking at.
 * The comparison column runs a second instance under a param prefix.
 */
export function useAnalyticsSelection({
	paramPrefix,
	listSourceType,
}: UseAnalyticsSelectionOptions = {}): AnalyticsSelection {
	const [searchParams, setSearchParams] = useSearchParams();

	const listsKey = prefixedParamKey(ANALYTICS_LISTS_PARAM_KEY, paramPrefix);
	const fromKey = prefixedParamKey(ANALYTICS_FROM_PARAM_KEY, paramPrefix);
	const toKey = prefixedParamKey(ANALYTICS_TO_PARAM_KEY, paramPrefix);
	const filterKey = prefixedParamKey(ANALYTICS_FILTER_PARAM_KEY, paramPrefix);
	const sendersKey = prefixedParamKey(ANALYTICS_SENDERS_PARAM_KEY, paramPrefix);

	// `?listId=` is a single-list deep link into the primary column only; a comparison
	// column must never swallow it.
	const listIdParam = paramPrefix ? null : searchParams.get("listId");
	// When the full selection was hydrated from `analytics_lists`, the `?listId=`
	// deep-link must not overwrite it — start "already preselected".
	const preselectedRef = useRef(!!searchParams.get(listsKey));

	// Hydrate from the URL on mount so a refresh / shared link restores the report;
	// subsequent changes flow back to the URL via the sync effect below.
	const [dateFromToValue, setDateFromToValue] = useState<DateValueType>(() =>
		parseDateRange(searchParams.get(fromKey), searchParams.get(toKey))
	);
	const [selectedLists, setSelectedLists] = useState<CustomListsAutocompleteForSequencerItem[]>(
		() => parseSelectedLists(searchParams.get(listsKey))
	);
	const [activeFilters, setActiveFilters] = useState<IFilterWithValues>(() =>
		parseFilters(searchParams.get(filterKey))
	);
	const [senderUserIds, setSenderUserIds] = useState<number[]>(() =>
		parseSenderUserIds(searchParams.get(sendersKey))
	);

	// Params a caller asked to set alongside the next selection write (see
	// `selectListsWithParams`). Applied once, by the sync effect below.
	const pendingParamsRef = useRef<Record<string, string> | null>(null);

	const listOptions = useAnalyticsListOptions({ sourceType: listSourceType });

	const { data: preselectedList } = useGetLeanListQuery(
		{ id: listIdParam ? +listIdParam : 0 },
		{ skip: !listIdParam || preselectedRef.current }
	);

	// Mirror every control back into the URL (`replace`, no history spam).
	//
	// Two columns run this hook side by side, so both correctness rules below matter:
	// the "has anything changed" check looks at **this column's keys only** (otherwise
	// each column sees the other's pending write as a change and they ping-pong), and
	// the write goes through the functional updater so it composes onto the freshest
	// params instead of a closed-over snapshot that would drop the other column's write.
	useEffect(() => {
		const serializedLists = serializeSelectedLists(selectedLists);
		const serializedFilters = serializeFilters(activeFilters);
		const { from, to } = serializeDateRange(dateFromToValue);

		const owned: [string, string | null][] = [
			[sendersKey, serializeSenderUserIds(senderUserIds)],
			[filterKey, serializedFilters],
			[listsKey, serializedLists],
			[fromKey, from],
			[toKey, to],
		];

		const isStale = owned.some(
			([key, value]) => (searchParams.get(key) ?? null) !== (value ?? null)
		);
		// The `?listId=` deep-link is consumed once the primary column has persisted its
		// selection, so it can't override `analytics_lists` on the next refresh.
		const shouldDropListId = !paramPrefix && !!serializedLists && searchParams.has("listId");
		const pendingParams = pendingParamsRef.current;

		if (!isStale && !shouldDropListId && !pendingParams) {
			return;
		}

		pendingParamsRef.current = null;

		setSearchParams(
			(current) => {
				const next = new URLSearchParams(current);

				owned.forEach(([key, value]) => setOrDeleteParam(next, key, value));

				// Ride along in the same write, so a caller's params cannot be clobbered by
				// this sync (or vice versa) in an intermediate commit.
				Object.entries(pendingParams ?? {}).forEach(([key, value]) => next.set(key, value));

				if (!paramPrefix && serializedLists) {
					next.delete("listId");
				}

				return next;
			},
			{ replace: true }
		);
	}, [activeFilters, selectedLists, dateFromToValue, senderUserIds, searchParams, setSearchParams]);

	useEffect(() => {
		if (preselectedList && !preselectedRef.current) {
			preselectedRef.current = true;
			setSelectedLists([toSelectedList(preselectedList)]);
		}
	}, [preselectedList]);

	const campaigns = useMemo<CampaignChip[]>(
		() =>
			selectedLists?.reduce<CampaignChip[]>((acc, list) => {
				if (!list.campaigns) {
					return acc;
				}

				return [
					...acc,
					...list.campaigns.map((campaign) => ({
						listId: list.id as number,
						listName: list.title,
						campaignId: campaign.campaignId,
						campaignName: campaign.campaignName,
						active: campaign.active,
					})),
				];
			}, []) || [],
		[selectedLists]
	);

	const payload = useMemo<AnalyticsRequest>(
		() => ({
			campaigns: selectedLists.map((list) => ({
				listId: list.id as number,
				campaignIds: list?.campaigns?.filter((c) => c.active).map((c) => c.campaignId),
			})),
			from: dateFromToValue?.startDate ?? undefined,
			to: dateFromToValue?.endDate ?? undefined,
			filters: Object.keys(activeFilters).length > 0 ? activeFilters : undefined,
			senderUserIds: senderUserIds.length > 0 ? senderUserIds : undefined,
		}),
		[selectedLists, dateFromToValue, activeFilters, senderUserIds]
	);

	const onCampaignToggle = useCallback((campaign: CampaignChip) => {
		setSelectedLists((prev) =>
			prev.map((selectedList) => {
				if (selectedList.id !== campaign.listId) {
					return selectedList;
				}

				return {
					...selectedList,
					campaigns: selectedList.campaigns?.map((c) =>
						c.campaignId === campaign.campaignId ? { ...c, active: !c.active } : c
					),
				};
			})
		);
	}, []);

	const onListSelect = useCallback((value: CustomListsAutocompleteForSequencerItem[]) => {
		setSelectedLists(value);
	}, []);

	const selectListsWithParams = useCallback(
		(value: CustomListsAutocompleteForSequencerItem[], params: Record<string, string>) => {
			pendingParamsRef.current = params;
			setSelectedLists(value);
		},
		[]
	);

	/**
	 * Turning a quick filter on narrows the options and selects every list it matches;
	 * turning it off widens the options and clears the selection back to "all lists".
	 *
	 * Clearing rather than un-selecting is deliberate: the previous selection was made *by*
	 * the filter, so keeping it would leave the report scoped to a set the user can no
	 * longer see the rule behind.
	 */
	const onListContentFilterToggle = useCallback(
		async (key: AnalyticsListContentFilterKey) => {
			const next = { ...listOptions.contentFilter, [key]: !listOptions.contentFilter[key] };

			listOptions.toggleContentFilter(key);

			if (!next.hasLeads && !next.hasCampaigns) {
				setSelectedLists([]);

				return;
			}

			setSelectedLists((await listOptions.fetchAllMatching(next)).map(toSelectedList));
		},
		[listOptions.contentFilter, listOptions.toggleContentFilter, listOptions.fetchAllMatching]
	);

	// "Lifetime" is a sentinel start date in the picker shortcuts; normalize it back to a
	// null range so the payload sends no date filter (all-time).
	const onDateChange = useCallback((value: DateValueType) => {
		if (isLifetimeStart(value?.startDate)) {
			setDateFromToValue({ startDate: null, endDate: null });

			return;
		}

		setDateFromToValue(value);
	}, []);

	const addFilter = useCallback((filter: IFilterWithValues) => {
		setActiveFilters((prev) => addOrReplaceFilter(prev, filter));
	}, []);

	const removeFilterByKey = useCallback((filterKey: string) => {
		setActiveFilters((prev) => removeFilter(prev, filterKey));
	}, []);

	const clearFilters = useCallback(() => setActiveFilters({}), []);

	return {
		dateFromToValue,
		onDateChange,
		listOptions,
		listData: listOptions.lists,
		selectedLists,
		onListSelect,
		onListContentFilterToggle,
		campaigns,
		onCampaignToggle,
		senderUserIds,
		onSenderChange: setSenderUserIds,
		selectListsWithParams,
		activeFilters,
		addFilter,
		removeFilter: removeFilterByKey,
		clearFilters,
		payload,
	};
}
